import type { FC, ReactNode } from "react";
import type { ApolloError } from "@apollo/client";
import type { SortingFn, FilterFn, SortingState } from "@tanstack/react-table";
import type {
  RuleType,
  MapDraftFields,
  CreateChangeData,
  EditChangeData,
  DraftStatus,
} from "../RulesDraft";
import type { SelectedScope } from "../../types/scope";
import type { FilterObject } from "../../AppView/FilterManagement/CommonFilterTypes";
import type { RuleKind, RuleUser } from "../types";
import type { RuleType as ExportRuleType } from "../RiskCustomizationUtils";

export type RulePageLink = "status" | "prioritization" | "sla";

export const RULE_KIND_TO_PAGE_LINK: Record<RuleKind, RulePageLink> = {
  scoring: "prioritization",
  sla: "sla",
  status: "status",
};

export interface RuleSelectionRule {
  scopesid?: string;
  scope_group?: string;
  filter?: {
    id?: string;
    name?: string;
    filter_data?: string;
  };
}

export interface RuleRowBase {
  id: string;
  name: string;
  enabled?: boolean;
  user?: Partial<RuleUser> | null;
  is_system?: boolean;
  selection_rule?: RuleSelectionRule;
  _draftStatus?: DraftStatus;
  // Resolved reference-data objects (not ids), stamped by `useRuleRows`.
  scope: SelectedScope | null;
  filter: FilterObject | null;
}

export interface RuleFormValues {
  id?: string;
  name: string;
  filter: string;
  scope: string;
  [key: string]: unknown;
}

// Per-rule-type fields the actions cell forwards to `RuleFormModal` on edit, on top
// of the generic id/name/scope/filter/value. Lets `RulesTable` stay agnostic of
// which rule type it renders (status carries comment/reopen_*, scoring method).
export interface RuleActionExtras {
  comment?: string;
  reopen_date?: string;
  reopen_reason?: string;
  method?: unknown;
}

// Converted by `RulesTable` into the `"rule_value"` `ColumnDef`. `accessor`
// returns the row's scalar so the shared `Table`'s grouping / default filter /
// actions-cell `getValue("rule_value")` behave as today.
export interface RuleValueColumn<TRow extends RuleRowBase> {
  header: string | (() => string);
  accessor: (row: TRow) => string | number | undefined;
  Cell: FC<{ row: TRow }>;
  sortingFn: SortingFn<TRow>;
  enableGrouping?: boolean;
  groupingLabel?: string;
  filterFn?: FilterFn<TRow>;
  enableColumnFilter?: boolean;
  width?: number;
}

export interface UseRuleRowsResult<TRow extends RuleRowBase> {
  rows: TRow[] | undefined;
  // True while the rules query is in flight OR the shared reference data
  // (scopes + filters) has not yet been hydrated into context.
  loading: boolean;
  error?: ApolloError;
}

// SLA naming footnote: the SLA GraphQL read root is `sla_preferences`, but the
// save field / draft key is `sla_rules` / `sla` — keep the two straight.
export interface RuleTypeConfig<TRow extends RuleRowBase> {
  key: RuleType;
  pageLink: RulePageLink;
  title: string | (() => string);
  description: ReactNode;
  permission: string;
  useRuleRows: () => UseRuleRowsResult<TRow>;
  valueColumn: RuleValueColumn<TRow>;
  ValueFormField: FC<{ savedRow?: TRow }>;
  modalEntityLabel: string;
  createModalClassName?: string;
  buildChangePayload: (
    form: RuleFormValues
  ) => CreateChangeData | EditChangeData;
  mapDraftFields: MapDraftFields<TRow>;
  exportConfig: {
    fileNamePrefix: string;
    title: string;
    valueLabel: string | (() => string);
    // `ExportRulesButton`'s rule type — note scoring's is `"score"`, not the
    // config `key` `"scoring"`.
    ruleType: ExportRuleType;
  };
  emptyState: {
    title: string;
    subtitle: string;
    noDatasourcesSubtitle?: string;
    ctaLabel: string;
    ctaDisabled?: boolean;
  };
  // Status/SLA gate on `hasDatasources`; scoring does NOT (its guard sits one
  // level up in `ScoringConfiguration`).
  requiresDatasources?: boolean;
  filterColumnWidth?: number;
  // Supplies the rule-type-specific edit fields for the actions cell.
  getRowActionData?: (row: TRow) => RuleActionExtras;
  getIsRowMutateDisabled?: (row: TRow) => boolean;
  defaultSort: SortingState;
}
